import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { SERVICES } from "@/data/site";
import { Loader2, Check, Sparkles } from "lucide-react";

export default function QuoteForm({ compact = false }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/leads", { ...form, source: "instant_quote" });
      setDone(true);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || "Couldn't send — please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="quote-form-success" className={`border border-[#8DE038] bg-[#8DE038]/10 p-6 ${compact ? '' : 'md:p-8'} text-center`}>
        <div className="w-14 h-14 rounded-full bg-[#8DE038] text-[#050806] flex items-center justify-center mx-auto mb-4"><Check size={28}/></div>
        <h3 className="font-display uppercase tracking-tighter text-2xl text-[#F0F4F1]">Got it! 🌱</h3>
        <p className="text-[#C8D4CB] mt-2 text-sm">We'll be in touch within 24 hours with your free quote. Check your inbox for confirmation.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className={`border border-[#1F3325] bg-[#0F1712]/95 backdrop-blur p-5 ${compact ? '' : 'md:p-7'} space-y-3`}>
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[#8DE038]"/>
        <span className="font-display uppercase tracking-[0.2em] text-xs text-[#8DE038]">Free quote · 60 seconds</span>
      </div>
      <h3 className="font-display uppercase tracking-tighter text-2xl md:text-3xl text-[#F0F4F1] leading-tight">Get an instant quote</h3>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="quote-name" required value={form.name} onChange={(e)=>update("name", e.target.value)} placeholder="Your name" className="gl-input"/>
        <input data-testid="quote-phone" required type="tel" value={form.phone} onChange={(e)=>update("phone", e.target.value)} placeholder="Phone" className="gl-input"/>
      </div>
      <input data-testid="quote-email" required type="email" value={form.email} onChange={(e)=>update("email", e.target.value)} placeholder="Email" className="gl-input"/>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="quote-postcode" value={form.postcode} onChange={(e)=>update("postcode", e.target.value)} placeholder="Postcode (e.g. LS1)" className="gl-input"/>
        <select data-testid="quote-service" value={form.service} onChange={(e)=>update("service", e.target.value)} className="gl-input">
          <option value="">Service (optional)</option>
          {SERVICES.map((s)=> <option key={s.slug} value={s.name}>{s.name}</option>)}
        </select>
      </div>
      <textarea data-testid="quote-message" rows={2} value={form.message} onChange={(e)=>update("message", e.target.value)} placeholder="Brief description (optional)" className="gl-input resize-none"/>
      {error && <div className="text-red-400 text-xs border border-red-500/40 bg-red-900/20 p-2">{error}</div>}
      <button data-testid="quote-submit" disabled={loading} type="submit" className="btn-primary w-full justify-center disabled:opacity-60">
        {loading ? <Loader2 className="animate-spin" size={18}/> : <>Get my free quote →</>}
      </button>
      <p className="text-[10px] text-[#6B7C71] tracking-widest uppercase text-center">No spam · Reply within 24h</p>
    </form>
  );
}
